import React, { useState } from "react";
import {
  FiCheckCircle,
  FiClock,
  FiPackage,
  FiTruck,
  FiXCircle
} from "react-icons/fi";
import { t } from "../../translations";
import "../styles/order-kanban-chef.css";

const OrderKanbanChef = ({ orders, updateOrder, lang }) => {
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Order status configuration
  const ORDER_STATUSES = {
    0: {
      title: t("order_selection.status_filter_options.pending", lang),
      color: "#FFA500",
      icon: <FiClock />
    },
    1: {
      title: t("order_selection.status_filter_options.ready", lang),
      color: "#1890FF",
      icon: <FiPackage />
    },
    2: {
      title: t("order_selection.status_filter_options.on_way", lang),
      color: "#722ED1",
      icon: <FiTruck />
    },
    3: {
      title: t("order_selection.status_filter_options.delivered", lang),
      color: "#52C41A",
      icon: <FiCheckCircle />
    },
    4: {
      title: t("order_selection.status_filter_options.canceled", lang),
      color: "#FF4D4F",
      icon: <FiXCircle />
    }
  };

  const getOrdersByStatus = (status) => {
    return orders.filter((o) => o.status === status);
  };

  const isValidStatusTransition = (currentStatus, newStatus) => {
    const validTransitions = {
      0: [1, 4], // Pending can go to Ready or Canceled
      1: [2, 4], // Ready can go to On Way or Canceled
      2: [3, 4], // On Way can go to Delivered or Canceled
      3: [], // Delivered is final
      4: [] // Canceled is final
    };

    return validTransitions[currentStatus]?.includes(newStatus) || false;
  };

  const handleDragStart = (e, order) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("orderId", order._id);
    setDraggedOrder(order);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    const orderId = e.dataTransfer.getData("orderId");

    if (!draggedOrder || draggedOrder._id !== orderId) return;

    if (!isValidStatusTransition(draggedOrder.status, newStatus)) {
      alert(
        t("order_kanban.invalid_transition", lang) ||
          `Cannot move order from ${ORDER_STATUSES[draggedOrder.status].title} to ${ORDER_STATUSES[newStatus].title}`
      );
      return;
    }

    await updateOrder(orderId, newStatus);
    setDraggedOrder(null);
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const renderOrderCard = (order) => {
    const statusConfig = ORDER_STATUSES[order.status];

    return (
      <div
        key={order._id}
        className="kanban-chef-card"
        draggable
        onDragStart={(e) => handleDragStart(e, order)}
        onClick={() => setSelectedOrder(order)}
        style={{
          borderLeft: `4px solid ${statusConfig.color}`
        }}
      >
        <div className="kanban-chef-card-header">
          <div
            className="kanban-chef-card-icon"
            style={{ color: statusConfig.color }}
          >
            {statusConfig.icon}
          </div>
          <strong>#{order._id.slice(-6)}</strong>
        </div>

        <div className="kanban-chef-card-body">
          <div className="kanban-chef-card-row">
            <span className="kanban-chef-card-label">
              {t("order_details.customer", lang)}:
            </span>
            <span>
              {order.user_id?.firstname} {order.user_id?.lastname}
            </span>
          </div>

          <div className="kanban-chef-card-row">
            <span className="kanban-chef-card-label">
              {t("order_details.delivery_date", lang)}:
            </span>
            <span>{formatDate(order.billing_details?.delivery_date)}</span>
          </div>

          <div className="kanban-chef-card-row">
            <span className="kanban-chef-card-label">
              {t("order_details.items", lang)}:
            </span>
            <span>{order.food?.length || 0} items</span>
          </div>

          <div className="kanban-chef-card-footer">
            <span className="kanban-chef-card-amount">
              {formatCurrency(order.billing_details?.amount)}
            </span>
            <span
              className="kanban-chef-card-status-badge"
              style={{ backgroundColor: statusConfig.color }}
            >
              {statusConfig.title}
            </span>
          </div>
        </div>
      </div>
    );
  };

  const renderKanbanColumn = (status) => {
    const statusConfig = ORDER_STATUSES[status];
    const columnOrders = getOrdersByStatus(status);

    return (
      <div className="kanban-chef-column" key={status}>
        <div
          className="kanban-chef-column-header"
          style={{ borderBottom: `3px solid ${statusConfig.color}` }}
        >
          <div className="kanban-chef-column-title">
            <div style={{ color: statusConfig.color }}>{statusConfig.icon}</div>
            <strong>{statusConfig.title}</strong>
          </div>
          <span
            className="kanban-chef-column-count"
            style={{ backgroundColor: statusConfig.color }}
          >
            {columnOrders.length}
          </span>
        </div>

        <div
          className="kanban-chef-column-body"
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, status)}
        >
          {columnOrders.length === 0 ? (
            <div className="kanban-chef-empty-column">
              {t("order_kanban.no_orders", lang) || "No orders"}
            </div>
          ) : (
            columnOrders.map((order) => renderOrderCard(order))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="kanban-chef-container">
      <div className="kanban-chef-board">
        {[0, 1, 2, 3, 4].map((status) => renderKanbanColumn(status))}
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div
          className="kanban-chef-modal-overlay"
          onClick={() => setSelectedOrder(null)}
        >
          <div
            className="kanban-chef-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="kanban-chef-modal-header">
              <h2>{t("order_details.title", lang) || "Order Details"}</h2>
              <button
                onClick={() => setSelectedOrder(null)}
                className="kanban-chef-modal-close"
              >
                ×
              </button>
            </div>
            <div className="kanban-chef-modal-body">
              <div className="kanban-chef-modal-section">
                <h3>
                  {t("order_details.order_info", lang) || "Order Information"}
                </h3>
                <p>
                  <strong>{t("order_details.order_id", lang)}:</strong>{" "}
                  {selectedOrder._id}
                </p>
                <p>
                  <strong>{t("order_details.status", lang)}:</strong>{" "}
                  {ORDER_STATUSES[selectedOrder.status].title}
                </p>
                <p>
                  <strong>{t("order_details.delivery_date", lang)}:</strong>{" "}
                  {formatDate(selectedOrder.billing_details?.delivery_date)}
                </p>
              </div>

              <div className="kanban-chef-modal-section">
                <h3>
                  {t("order_details.customer_info", lang) ||
                    "Customer Information"}
                </h3>
                <p>
                  <strong>{t("order_details.name", lang)}:</strong>{" "}
                  {selectedOrder.user_id?.firstname}{" "}
                  {selectedOrder.user_id?.lastname}
                </p>
                <p>
                  <strong>{t("order_details.phone", lang)}:</strong>{" "}
                  {selectedOrder.delivery_details?.phone_number}
                </p>
                <p>
                  <strong>{t("order_details.address", lang)}:</strong>{" "}
                  {selectedOrder.delivery_details?.street_name}
                </p>
                {selectedOrder.delivery_details?.instruction && (
                  <p>
                    <strong>{t("order_details.instruction", lang)}:</strong>{" "}
                    {selectedOrder.delivery_details.instruction}
                  </p>
                )}
              </div>

              <div className="kanban-chef-modal-section">
                <h3>{t("order_details.items", lang) || "Order Items"}</h3>
                {selectedOrder.food?.map((item, index) => (
                  <div key={index} className="kanban-chef-modal-item">
                    <span>
                      {item.quantity}x {item.name}
                    </span>
                    <span>{formatCurrency(item.price * item.quantity)}</span>
                  </div>
                ))}
              </div>

              <div className="kanban-chef-modal-section">
                <h3>{t("order_details.billing", lang) || "Billing"}</h3>
                <div className="kanban-chef-modal-item">
                  <span>{t("order_details.subtotal", lang)}:</span>
                  <span>
                    {formatCurrency(
                      selectedOrder.billing_details?.amount -
                        selectedOrder.billing_details?.tax -
                        selectedOrder.billing_details?.delivery_price
                    )}
                  </span>
                </div>
                <div className="kanban-chef-modal-item">
                  <span>{t("order_details.delivery", lang)}:</span>
                  <span>
                    {formatCurrency(
                      selectedOrder.billing_details?.delivery_price
                    )}
                  </span>
                </div>
                <div className="kanban-chef-modal-item">
                  <span>{t("order_details.tax", lang)}:</span>
                  <span>
                    {formatCurrency(selectedOrder.billing_details?.tax)}
                  </span>
                </div>
                <div className="kanban-chef-modal-item kanban-chef-modal-total">
                  <span>
                    <strong>{t("order_details.total", lang)}:</strong>
                  </span>
                  <span>
                    <strong>
                      {formatCurrency(selectedOrder.billing_details?.amount)}
                    </strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderKanbanChef;
